import {
  DefaultParamType,
  T,
  TFnType,
  TranslationKey,
  useTranslate,
} from '@tolgee/react';
import { StaticImageData } from 'next/image';
import React from 'react';

import drawingGridExample from '../assets/images/grid.png';
import gridResponseExample from '../assets/images/gridResponseExample.png';
import podiumExample from '../assets/images/podium.png';
import pollExample from '../assets/images/poll.png';
import textGridExample from '../assets/images/textgrid.png';
import wordcloudExample from '../assets/images/wordcloudexample.png';
import theme from '../assets/theme';
import { RoundType, SlideType } from '../services/backendService/types';

export const toSubjects = (
  t: TFnType<DefaultParamType, string, TranslationKey>,
  type?: string | RoundType,
  plural?: boolean,
) => {
  if (type === 'questions') return t(`Question${plural ? 's' : ''}`); /** @depricated */
  if (type === 'answers') return t(`Answer${plural ? 's' : ''}`);
  if (type === 'aiAssessedAnswers') return t(`Answer${plural ? 's' : ''}`);
  if (type === 'aiAssessedImageAnswers') return t(`Drawing${plural ? 's' : ''}`);
  if (type === 'wordcloud') return t(`Answer${plural ? 's' : ''}`);
  if (type === 'ideas') return t(`Idea${plural ? 's' : ''}`);
  if (type === 'canvas') return t(`Drawing${plural ? 's' : ''}`);
  if (type === 'poll') return t(`Vote${plural ? 's' : ''}`);
  if (type === 'voting') return `Vote${plural ? 's' : ''}`;
  if (type === 'videos') return t(`Video${plural ? 's' : ''}`); /** @depricated */
  return '';
};

const TypeSelectorOptions = [
  'poll',
  'wordcloud',
  'canvas',
  'answers',
  'aiAssessedAnswers',
  'aiAssessedImageAnswers',
] as const;
type TypeSelectorOption = typeof TypeSelectorOptions[number];

export function useActivityMenuItems(): Array<{
  selectorOption: TypeSelectorOption;
  activity: {
    title: string;
    paragraph: React.ReactNode;
    alternateParagraph?: React.ReactNode;
    image: StaticImageData | null;
    alternateImage?: StaticImageData;
    icon: string;
    beta?: boolean; // idk if this is used at all
  };
}> {
  const { t } = useTranslate();
  return [
    {
      selectorOption: 'poll',
      activity: {
        title: t('Poll', 'Poll'),
        paragraph: (
          <T
            keyName="Your participants choose between up to 10 options. The results are shown on the big screen."
            defaultValue="Your participants choose between up to 10 options. The results are shown on the big screen."
          />
        ),
        image: pollExample,
        icon: 'PollIcon',
      },
    },
    {
      selectorOption: 'wordcloud',
      activity: {
        title: t('Word Cloud', 'Word Cloud'),
        paragraph: (
          <T
            keyName="Your participants write short answers for up to 30 characters. The results are shown as a word cloud on the big screen."
            defaultValue="Your participants write short answers for up to 30 characters. The results are shown as a word cloud on the big screen."
          />
        ),
        image: wordcloudExample,
        icon: 'CloudIcon',
      },
    },
    {
      selectorOption: 'canvas',
      activity: {
        title: t('Drawings', 'Drawings'),
        paragraph: (
          <T
            keyName="Your participants answer with a drawing. The results will be displayed as a podium."
            defaultValue="Your participants answer with a drawing. The results will be displayed as a podium."
          />
        ),
        alternateParagraph: (
          <T
            keyName="Your participants answer with a drawing. The results are displayed in a grid."
            defaultValue="Your participants answer with a drawing. The results are displayed in a grid."
          />
        ),
        image: podiumExample,
        alternateImage: drawingGridExample,
        icon: 'DrawingsIcon',
      },
    },
    {
      selectorOption: 'answers',
      activity: {
        title: t('Open Question', 'Open Question'),
        paragraph: (
          <T
            keyName="Your participants answer your question with longer sentences. Participants can vote for the best answers and the results are displayed as a podium."
            defaultValue="Your participants answer your question with longer sentences. Participants can vote for the best answers and the results are displayed as a podium."
          />
        ),
        alternateParagraph:
          'Your participants answer your question with longer sentences. The results will be displayed in a grid',
        image: podiumExample,
        alternateImage: textGridExample,
        icon: 'QuestionsIcon',
      },
    },
    {
      selectorOption: 'aiAssessedAnswers',
      activity: {
        title: t('AI Feedback', 'AI Feedback'),
        // paragraph:
        //   'Deltagerne svarer på spørsmålet ditt med lengre setninger. Svarene kan vises i et rutenett eller legg til avstemming for å gjøre det til en konkurranse!',
        paragraph: (
          <T
            keyName="Your participants answer your question with longer sentences. Choose a character limit between 475 up to 5000. You will get the chance to instantly assess the responses with the help of AI."
            defaultValue="Your participants answer your question with longer sentences. Choose a character limit between 475 up to 5000. You will get the chance to instantly assess the responses with the help of AI."
          />
        ),
        alternateParagraph:
          'Your participants answer your question with longer sentences. Choose a character limit between 475 up to 5000. The results will be displayed in a grid. ',
        image: gridResponseExample,
        alternateImage: textGridExample,
        icon: 'InstantFeedbackIcon',
        beta: false,
      },
    },
    {
      selectorOption: 'aiAssessedImageAnswers',
      activity: {
        title: t('AI Whiteboard', 'AI Whiteboard'),
        paragraph: (
          <T
            keyName="Your students answer with a drawing. You will get the chance to instantly assess the responses with the help of AI."
            defaultValue="Your students answer with a drawing. You will get the chance to instantly assess the responses with the help of AI."
          />
        ),
        alternateParagraph: (
          <T
            keyName="Your students answer with a drawing. You will get the chance to instantly assess the responses with the help of AI."
            defaultValue="Your students answer with a drawing. You will get the chance to instantly assess the responses with the help of AI."
          />
        ),
        image: drawingGridExample,
        alternateImage: drawingGridExample,
        icon: 'InstantDrawingFeedbackIcon',
        beta: false,
      },
    },
  ];
}

export function useActivityForSlideType(
  type: RoundType | SlideType | string,
): {
  title: string;
  paragraph: React.ReactNode;
  alternateParagraph?: React.ReactNode;
  image: StaticImageData | null;
  alternateImage?: StaticImageData;
  icon: string;
  beta?: boolean; // idk if this is used at all
} | null {
  const { t } = useTranslate();
  if (type === 'slide') {
    return {
      title: t('Slide', 'Slide'),
      paragraph: '',
      image: null,
      icon: 'TitleImageSvg',
    };
  }

  if (type === 'header') {
    return {
      title: t('Header', 'Header'),
      // paragraph: 'Enkelt lysbilde med støtte for tittel og undertittel.',
      paragraph: (
        <T
          keyName="Simple slide with support for a title and a subtitle"
          defaultValue="Simple slide with support for a title and a subtitle"
        />
      ),
      image: null,
      icon: 'SlideHeaderSvg',
    };
  }
  if (type === 'header_text' || type === 'bullets') {
    return {
      title: t('Text', 'Text'),
      // paragraph: 'Enkelt lysbilde med støtte for tittel og undertittel.',
      paragraph: (
        <T
          keyName="Simple slide with support for a title and text"
          defaultValue="Simple slide with support for a title and text"
        />
      ),
      image: null,
      icon: 'SlideHeaderText',
    };
  }

  if (type === 'image') {
    return {
      title: t('Title & media', 'Title & media'),
      // paragraph:
      //   'Lysbilde for media med støtte for tittel. Legg til bilder, GIFs, og youtube-videoer',
      paragraph: (
        <T
          keyName="Slide with media and a title. Add images, gif's and youtube videos"
          defaultValue="Slide with media and a title. Add images, gif's and youtube videos"
        />
      ),
      image: null,
      icon: 'TitleImageSvg',
    };
  }
  if (type === 'image_text') {
    return {
      title: t('Media & text', 'Media & text'),
      // paragraph:
      //   'Lysbilde for media med støtte for tittel. Legg til bilder, GIFs, og youtube-videoer',
      paragraph: (
        <T
          keyName="Slide with media, text and a title. Add images, gif's and youtube videos"
          defaultValue="Slide with media, text and a title. Add images, gif's and youtube videos"
        />
      ),
      image: null,
      icon: 'MediaTextSvg',
    };
  }
  if (type === 'text_image') {
    return {
      title: t('Text & media', 'Text & media'),
      // paragraph:
      //   'Lysbilde for media med støtte for tittel. Legg til bilder, GIFs, og youtube-videoer',
      paragraph: (
        <T
          keyName="Slide with media, text and a title. Add images, gif's and youtube videos"
          defaultValue="Slide with media, text and a title. Add images, gif's and youtube videos"
        />
      ),
      image: null,
      icon: 'TextMediaSvg',
    };
  }
  if (type === 'fullscreen_media') {
    return {
      title: t('Fullscreen media', 'Fullscreen media'),
      // paragraph:
      //   'Lysbilde for media som skal være i fullskjerm. Bilder, GIFs og youtube-videoer er støttet',
      paragraph: (
        <T
          keyName="Slide for a full screen media. Add an image, GIF or youtube videos"
          defaultValue="Slide for a full screen media. Add an image, GIF or youtube videos"
        />
      ),
      image: null,
      icon: 'YoutubeSvg',
    };
  }
  if (type === 'poll') {
    return {
      title: t('Poll', 'Poll'),
      // paragraph:
      //   'Deltagerne velger blant opp til 10 valg. Resultatene blir vist på storskjermen',
      paragraph: (
        <T
          keyName="Your participants choose between up to 10 options. The results are shown on the big screen."
          defaultValue="Your participants choose between up to 10 options. The results are shown on the big screen."
        />
      ),
      image: pollExample,
      icon: 'PollIcon',
    };
  }
  if (type === 'videos') {
    /** @depricated */
    return {
      title: t('Video response', 'Video response'),
      // paragraph:
      //   'Deltagerne svarer med korte videosnutter laget med et valgfritt videoredigeringsverktøy',
      paragraph: (
        <T
          keyName="Your participants respond with a very short video (15 seconds) made with their favourite video editing tool. The results will be displayed in a grid."
          defaultValue="Your participants respond with a very short video (15 seconds) made with their favourite video editing tool. The results will be displayed in a grid."
        />
      ),
      image: gridResponseExample,
      alternateImage: textGridExample,
      icon: 'VideoActivitySvg',
      beta: true,
    };
  }
  // TODO: Groupmaker experience
  if (type === 'groupmaker') {
    return {
      title: t('Group', 'Group'),
      paragraph: 'Create groups',
      image: null,
      icon: 'GroupSvg',
    };
  }

  if (type === 'wordcloud') {
    return {
      title: t('Word Cloud', 'Word Cloud'),
      // paragraph:
      //   'Deltagerne skriver inn korte svar på maks 30 tegn. Resultatene vises som en ordsky på storskjermen.',
      paragraph: (
        <T
          keyName="Your participants write short answers for up to 30 characters. The results are shown as a word cloud on the big screen."
          defaultValue="Your participants write short answers for up to 30 characters. The results are shown as a word cloud on the big screen."
        />
      ),
      image: wordcloudExample,
      icon: 'CloudIcon',
    };
  }
  if (type === 'answers') {
    return {
      title: t('Open Question', 'Open Question'),
      // paragraph:
      //   'Deltagerne svarer på spørsmålet ditt med lengre setninger. Svarene kan vises i et rutenett eller legg til avstemming for å gjøre det til en konkurranse!',
      paragraph: (
        <T
          keyName="Your participants answer your question with longer sentences. Participants can vote for the best answers and the results are displayed as a podium."
          defaultValue="Your participants answer your question with longer sentences. Participants can vote for the best answers and the results are displayed as a podium."
        />
      ),
      alternateParagraph:
        'Your participants answer your question with longer sentences. The results will be displayed in a grid',
      image: podiumExample,
      alternateImage: textGridExample,
      icon: 'QuestionsIcon',
    };
  }
  if (type === 'aiAssessedAnswers') {
    return {
      title: t('AI Feedback', 'AI Feedback'),
      // paragraph:
      //   'Deltagerne svarer på spørsmålet ditt med lengre setninger. Svarene kan vises i et rutenett eller legg til avstemming for å gjøre det til en konkurranse!',
      paragraph: (
        <T
          keyName="Your participants answer your question with longer sentences. Choose a character limit between 475 up to 5000. You will get the chance to instantly assess the responses with the help of AI."
          defaultValue="Your participants answer your question with longer sentences. Choose a character limit between 475 up to 5000. You will get the chance to instantly assess the responses with the help of AI."
        />
      ),
      alternateParagraph:
        'Your participants answer your question with longer sentences. Choose a character limit between 475 up to 5000. The results will be displayed in a grid. ',
      image: gridResponseExample,
      alternateImage: textGridExample,
      icon: 'InstantFeedbackIcon',
      beta: false,
    };
  }
  if (type === 'aiAssessedImageAnswers') {
    return {
      title: t('AI Whiteboard', 'AI Whiteboard'),
      // paragraph:
      //   'Deltagerne svarer på spørsmålet ditt med lengre setninger. Svarene kan vises i et rutenett eller legg til avstemming for å gjøre det til en konkurranse!',
      paragraph: (
        <T
          keyName="Your students answer with a drawing. You will get the chance to instantly assess the responses with the help of AI."
          defaultValue="Your students answer with a drawing. You will get the chance to instantly assess the responses with the help of AI."
        />
      ),
      alternateParagraph: (
        <T
          keyName="Your students answer with a drawing. You will get the chance to instantly assess the responses with the help of AI."
          defaultValue="Your students answer with a drawing. You will get the chance to instantly assess the responses with the help of AI."
        />
      ),
      image: gridResponseExample,
      alternateImage: textGridExample,
      icon: 'InstantDrawingFeedbackIcon',
      beta: false,
    };
  }
  if (type === 'canvas') {
    return {
      title: t('Drawings', 'Drawings'),
      // paragraph:
      //   'Deltagerne tegner i en fri tavle, løser problemer og svarer på spørsmål. Tegn, reflekter og se svarene i et rutenett eller legg til avstemning for å gjøre det til en konkurranse!',
      paragraph: (
        <T
          keyName="Your participants answer with a drawing. The results will be displayed as a podium."
          defaultValue="Your participants answer with a drawing. The results will be displayed as a podium."
        />
      ),
      alternateParagraph: (
        <T
          keyName="Your participants answer with a drawing. The results are displayed in a grid."
          defaultValue="Your participants answer with a drawing. The results are displayed in a grid."
        />
      ),
      image: podiumExample,
      alternateImage: drawingGridExample,
      icon: 'DrawingsIcon',
    };
  }
  if (type === 'questions') {
    /** @depricated */
    return {
      title: t('Q&A', 'Q&A'),
      // paragraph:
      //   'Deltagerne stiller spørsmål til deg. Ta inn spørsmål på slutten av et foredrag eller tren på å stille gode spørsmål. Legg til avstemming for å demokratisk finne de beste spørsmålene som flest ønsker svar på.',
      paragraph: (
        <T
          keyName="Run a Q&A session where everyone gets the chance to ask you a question. Participants can vote for the best questions and the results will be displayed as a podium."
          defaultValue="Run a Q&A session where everyone gets the chance to ask you a question. Participants can vote for the best questions and the results will be displayed as a podium."
        />
      ),
      alternateParagraph: (
        <T
          keyName="Your participants ask you questions. All the questions will be displayed in a grid."
          defaultValue="Your participants ask you questions. All the questions will be displayed in a grid."
        />
      ),
      image: podiumExample,
      alternateImage: textGridExample,
      icon: 'FlashIcon',
    };
  }

  return null;
}

export function getActivityForSlideType(
  type: RoundType | SlideType | string,
): {
  title: React.ReactNode;
  paragraph: React.ReactNode;
  alternateParagraph?: React.ReactNode;
  image: StaticImageData | null;
  alternateImage?: StaticImageData;
  icon: string;
  beta?: boolean; // idk if this is used at all
} | null {
  if (type === 'slide') {
    return {
      title: <T keyName="Slide" defaultValue="Slide" />,
      paragraph: '',
      image: null,
      icon: 'TitleImageSvg',
    };
  }

  if (type === 'header') {
    return {
      title: <T keyName="Header" defaultValue="Header" />,
      // paragraph: 'Enkelt lysbilde med støtte for tittel og undertittel.',
      paragraph: (
        <T
          keyName="Simple slide with support for a title and a subtitle"
          defaultValue="Simple slide with support for a title and a subtitle"
        />
      ),
      image: null,
      icon: 'SlideHeaderSvg',
    };
  }
  if (type === 'header_text' || type === 'bullets') {
    return {
      title: <T keyName="Text" defaultValue="Text" />,
      // paragraph: 'Enkelt lysbilde med støtte for tittel og undertittel.',
      paragraph: (
        <T
          keyName="Simple slide with support for a title and text"
          defaultValue="Simple slide with support for a title and text"
        />
      ),
      image: null,
      icon: 'SlideHeaderText',
    };
  }

  if (type === 'image') {
    return {
      title: <T keyName="Title & media" defaultValue="Title & media" />,
      // paragraph:
      //   'Lysbilde for media med støtte for tittel. Legg til bilder, GIFs, og youtube-videoer',
      paragraph: (
        <T
          keyName="Slide with media and a title. Add images, gif's and youtube videos"
          defaultValue="Slide with media and a title. Add images, gif's and youtube videos"
        />
      ),
      image: null,
      icon: 'TitleImageSvg',
    };
  }
  if (type === 'image_text') {
    return {
      title: <T keyName="Media & text" defaultValue="Media & text" />,
      // paragraph:
      //   'Lysbilde for media med støtte for tittel. Legg til bilder, GIFs, og youtube-videoer',
      paragraph: (
        <T
          keyName="Slide with media, text and a title. Add images, gif's and youtube videos"
          defaultValue="Slide with media, text and a title. Add images, gif's and youtube videos"
        />
      ),
      image: null,
      icon: 'MediaTextSvg',
    };
  }
  if (type === 'text_image') {
    return {
      title: <T keyName="Text & media" defaultValue="Text & media" />,
      // paragraph:
      //   'Lysbilde for media med støtte for tittel. Legg til bilder, GIFs, og youtube-videoer',
      paragraph: (
        <T
          keyName="Slide with media, text and a title. Add images, gif's and youtube videos"
          defaultValue="Slide with media, text and a title. Add images, gif's and youtube videos"
        />
      ),
      image: null,
      icon: 'TextMediaSvg',
    };
  }
  if (type === 'fullscreen_media') {
    return {
      title: <T keyName="Fullscreen media" defaultValue="Fullscreen media" />,
      // paragraph:
      //   'Lysbilde for media som skal være i fullskjerm. Bilder, GIFs og youtube-videoer er støttet',
      paragraph: (
        <T
          keyName="Slide for a full screen media. Add an image, GIF or youtube videos"
          defaultValue="Slide for a full screen media. Add an image, GIF or youtube videos"
        />
      ),
      image: null,
      icon: 'YoutubeSvg',
    };
  }
  if (type === 'poll') {
    return {
      title: <T keyName="Poll" defaultValue="Poll" />,
      // paragraph:
      //   'Deltagerne velger blant opp til 10 valg. Resultatene blir vist på storskjermen',
      paragraph: (
        <T
          keyName="Your participants choose between up to 10 options. The results are shown on the big screen."
          defaultValue="Your participants choose between up to 10 options. The results are shown on the big screen."
        />
      ),
      image: pollExample,
      icon: 'PollIcon',
    };
  }
  if (type === 'videos') {
    /** @depricated */
    return {
      title: <T keyName="Video response" defaultValue="Video response" />,
      // paragraph:
      //   'Deltagerne svarer med korte videosnutter laget med et valgfritt videoredigeringsverktøy',
      paragraph: (
        <T
          keyName="Your participants respond with a very short video (15 seconds) made with their favourite video editing tool. The results will be displayed in a grid."
          defaultValue="Your participants respond with a very short video (15 seconds) made with their favourite video editing tool. The results will be displayed in a grid."
        />
      ),
      image: gridResponseExample,
      alternateImage: textGridExample,
      icon: 'VideoActivitySvg',
      beta: true,
    };
  }
  // TODO: Groupmaker experience
  if (type === 'groupmaker') {
    return {
      title: <T keyName="Group" defaultValue="Group" />,
      paragraph: 'Create groups',
      image: null,
      icon: 'GroupSvg',
    };
  }

  if (type === 'wordcloud') {
    return {
      title: <T keyName="Word cloud" defaultValue="Word Cloud" />,
      // paragraph:
      //   'Deltagerne skriver inn korte svar på maks 30 tegn. Resultatene vises som en ordsky på storskjermen.',
      paragraph: (
        <T
          keyName="Your participants write short answers for up to 30 characters. The results are shown as a word cloud on the big screen."
          defaultValue="Your participants write short answers for up to 30 characters. The results are shown as a word cloud on the big screen."
        />
      ),
      image: wordcloudExample,
      icon: 'CloudIcon',
    };
  }
  if (type === 'answers') {
    return {
      title: <T keyName="Open Question" defaultValue="Open Question" />,
      // paragraph:
      //   'Deltagerne svarer på spørsmålet ditt med lengre setninger. Svarene kan vises i et rutenett eller legg til avstemming for å gjøre det til en konkurranse!',
      paragraph: (
        <T
          keyName="Your participants answer your question with longer sentences. Participants can vote for the best answers and the results are displayed as a podium."
          defaultValue="Your participants answer your question with longer sentences. Participants can vote for the best answers and the results are displayed as a podium."
        />
      ),
      alternateParagraph:
        'Your participants answer your question with longer sentences. The results will be displayed in a grid',
      image: podiumExample,
      alternateImage: textGridExample,
      icon: 'QuestionsIcon',
    };
  }
  if (type === 'aiAssessedAnswers') {
    return {
      title: <T keyName="AI Feedback" defaultValue="AI Feedback" />,
      // paragraph:
      //   'Deltagerne svarer på spørsmålet ditt med lengre setninger. Svarene kan vises i et rutenett eller legg til avstemming for å gjøre det til en konkurranse!',
      paragraph: (
        <T
          keyName="Your participants answer your question with longer sentences. Choose a character limit between 475 up to 5000. You will get the chance to instantly assess the responses with the help of AI."
          defaultValue="Your participants answer your question with longer sentences. Choose a character limit between 475 up to 5000. You will get the chance to instantly assess the responses with the help of AI."
        />
      ),
      alternateParagraph:
        'Your participants answer your question with longer sentences. Choose a character limit between 475 up to 5000. The results will be displayed in a grid. ',
      image: gridResponseExample,
      alternateImage: textGridExample,
      icon: 'InstantFeedbackIcon',
      beta: false,
    };
  }
  if (type === 'aiAssessedImageAnswers') {
    return {
      title: <T keyName="AI Whiteboard" defaultValue="AI Whiteboard" />,
      // paragraph:
      //   'Deltagerne svarer på spørsmålet ditt med lengre setninger. Svarene kan vises i et rutenett eller legg til avstemming for å gjøre det til en konkurranse!',
      paragraph: (
        <T
          keyName="Your students answer with a drawing. You will get the chance to instantly assess the responses with the help of AI."
          defaultValue="Your students answer with a drawing. You will get the chance to instantly assess the responses with the help of AI."
        />
      ),
      alternateParagraph: (
        <T
          keyName="Your students answer with a drawing. You will get the chance to instantly assess the responses with the help of AI."
          defaultValue="Your students answer with a drawing. You will get the chance to instantly assess the responses with the help of AI."
        />
      ),
      image: gridResponseExample,
      alternateImage: textGridExample,
      icon: 'InstantDrawingFeedbackIcon',
      beta: false,
    };
  }
  if (type === 'canvas') {
    return {
      title: <T keyName="Drawings" defaultValue="Drawings" />,
      // paragraph:
      //   'Deltagerne tegner i en fri tavle, løser problemer og svarer på spørsmål. Tegn, reflekter og se svarene i et rutenett eller legg til avstemning for å gjøre det til en konkurranse!',
      paragraph: (
        <T
          keyName="Your participants answer with a drawing. The results will be displayed as a podium."
          defaultValue="Your participants answer with a drawing. The results will be displayed as a podium."
        />
      ),
      alternateParagraph: (
        <T
          keyName="Your participants answer with a drawing. The results are displayed in a grid."
          defaultValue="Your participants answer with a drawing. The results are displayed in a grid."
        />
      ),
      image: podiumExample,
      alternateImage: drawingGridExample,
      icon: 'DrawingsIcon',
    };
  }
  if (type === 'questions') {
    /** @depricated */
    return {
      title: <T keyName="Q&A" defaultValue="Q&A" />,
      // paragraph:
      //   'Deltagerne stiller spørsmål til deg. Ta inn spørsmål på slutten av et foredrag eller tren på å stille gode spørsmål. Legg til avstemming for å demokratisk finne de beste spørsmålene som flest ønsker svar på.',
      paragraph: (
        <T
          keyName="Run a Q&A session where everyone gets the chance to ask you a question. Participants can vote for the best questions and the results will be displayed as a podium."
          defaultValue="Run a Q&A session where everyone gets the chance to ask you a question. Participants can vote for the best questions and the results will be displayed as a podium."
        />
      ),
      alternateParagraph: (
        <T
          keyName="Your participants ask you questions. All the questions will be displayed in a grid."
          defaultValue="Your participants ask you questions. All the questions will be displayed in a grid."
        />
      ),
      image: podiumExample,
      alternateImage: textGridExample,
      icon: 'FlashIcon',
    };
  }

  return null;
}

export function generateUniqueId() {
  return Math.floor(Math.random() * Date.now());
}
const INDEX_COLORS = [
  theme.colors.orange,
  theme.colors.green,
  theme.colors.red,
  theme.colors.blue,
  theme.colors.yellow,
  theme.colors.blueDark,
  theme.colors.aqua,
  theme.colors.black,
  theme.colors.eraserPink,
  theme.colors.purple,
];
export const getIndexToColor = (index?: number) => {
  if (index === undefined) return '';
  if (index > INDEX_COLORS.length - 1) return INDEX_COLORS[0];
  return INDEX_COLORS[index];
};
export const generateRoundOptionColor = (existingColors: string[]) => {
  const availableColors = INDEX_COLORS.filter(color => !existingColors.includes(color));

  if (availableColors.length === 0) {
    throw new Error('Not any more colors');
  }
  return availableColors[0];
};

export const MOBILE_TO_DESKTOP_BREAKPOINT_PX = 900;
